import { TDrawModel } from '../draw.model';
import { DRAW_MODEL_UTILS } from './utils';
import { DRAW_TASKS } from './draw/draw.tasks';
import { TEntityActionCb } from '../timeline.types';
import { DRAW_ELEMENTS } from './draw/element.tasks';
import { DRAW_LINKS } from './draw/link.tasks';
import { DRAW_OVERLAYS } from './draw/overlay.tasks';
import * as d3 from 'd3';

export const createTimelineChart = (drawModel: TDrawModel, entityEventCb: TEntityActionCb) => {
  const timeline = DRAW_TASKS.drawTimeline(drawModel);
  DRAW_TASKS.drawBackground(drawModel, timeline);
  DRAW_TASKS.drawHeader(drawModel, timeline);
  const body = DRAW_TASKS.drawBody(drawModel, timeline);
  const foreground = DRAW_TASKS.drawForeground(drawModel, timeline);

  const content = DRAW_TASKS.drawContent(drawModel, body);
  const leftNav = drawModel.timelineModel.hideLeftPanel ? undefined : DRAW_TASKS.drawLefNav(drawModel, body);

  const entityGroup = content
    .selectAll('.entity-group')
    .data(drawModel.timelineModel.entities)
    .join('svg')
    .classed('entity-group', true)
    .attr('height', (d) => d.entities.length * drawModel.layout.entityHeight);

  entityGroup.attr('y', (_, i) => {
    const entityGroupList = entityGroup.data();
    const updatedY = entityGroupList.reduce((acc, val, idx) => {
      const updatedAcc = idx < i ? acc + drawModel.layout.entityHeight * val.entities.length : acc;
      return updatedAcc;
    }, 0);

    return updatedY;
  });

  const groupHolidays = entityGroup
    .selectAll('.group-holidays')
    .data((d) => [d])
    .join('g')
    .classed('group-holidays', true);

  // Clean up
  groupHolidays.selectAll('.holiday').remove();
  groupHolidays
    .selectAll('holiday')
    .data((d) => d.holidays)
    .join((enter) => {
      const svg = enter.append('svg').attr('height', '100%');
      svg
        .append('rect')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('fill', `url(#${drawModel.defsIds.checkered})`);
      return svg;
    })
    .classed('holiday', true)
    .attr('width', drawModel.layout.widthOfDay)
    .attr('x', (d) => {
      const xPosition = DRAW_MODEL_UTILS.getPostionByDate(d, drawModel);
      return xPosition;
    });

  const entity = entityGroup
    .selectAll('.entity')
    .data((d) => d.entities)
    .join('g')
    .classed('entity', true)
    .attr('transform', (_, i) => `translate(0, ${drawModel.layout.entityHeight * i - drawModel.layout.offsetBottom})`);

  const elements = entity
    .selectAll('.element')
    .data((d) => d.elements)
    .join('svg')
    .classed('element', true);

  DRAW_ELEMENTS.drawElements(elements, drawModel, entityEventCb);

  // Draw strip links using optimized function
  DRAW_LINKS.drawStripLinks(entity, drawModel);

  elements
    .attr('x', (d) => {
      const [startPosition] = DRAW_MODEL_UTILS.getRangePosition(d.range, drawModel);
      return startPosition;
    })
    .attr('y', (d) => DRAW_MODEL_UTILS.calculateElementYPosition(d, drawModel))
    .attr('width', (d) => {
      const [startPosition, endPosition] = DRAW_MODEL_UTILS.getRangePosition(d.range, drawModel);
      if (startPosition === endPosition) {
        return drawModel.layout.widthOfDay;
      }

      // NOTE: used to show two markers on the same date
      if (d.range.startDate === d.range.endDate && d.isHalfDay) {
        return (endPosition - startPosition) / 2;
      }
      return endPosition - startPosition;
    })
    .attr('height', (d) => {
      return (drawModel.layout.stackHeights[d.stackCount] * (d.heightInPercent ?? 100)) / 100;
    })
    .style('cursor', (d) => (d.type === 'STRIP' ? 'pointer' : 'auto'));

  const leftNavGroupItems = leftNav
    ?.selectAll('.entity-group-info')
    .data(drawModel.timelineModel.entities)
    .join('g')
    .classed('entity-group-info', true);
  // Apply visual overlays to disabled entities - grays out timeline rows and left nav items
  DRAW_OVERLAYS.drawDisabledEntityOverlays(
    [
      { selection: entityGroup, className: 'entity-disabled-overlay' },
      ...(leftNavGroupItems ? [{ selection: leftNavGroupItems, className: 'left-nav-disabled-overlay' as const }] : []),
    ],
    drawModel,
  );

  leftNavGroupItems?.attr('transform', (_, i) => {
    const entityGroupList = leftNavGroupItems.data();
    const updatedY = entityGroupList.reduce((acc, val, idx) => {
      const updatedAcc = idx < i ? acc + drawModel.layout.entityHeight * val.entities.length : acc;
      // console.log('Calulation', idx < i, updatedAcc, idx, i, val.entities.length, drawModel.layout.entityHeight);
      return updatedAcc;
    }, 0);
    return `translate(0,${updatedY})`;
  });
  const leftNavItems = leftNavGroupItems
    ?.selectAll('.entity-info')
    .data((d) => d.entities)
    .join('g')
    .classed('entity-info', true)
    .attr('transform', (_, i) => {
      return `translate(0,${drawModel.layout.entityHeight * i})`;
    });
  leftNavItems?.attr('y', (_, i) => {
    const entityGroupList = entityGroup.data();
    const updatedY = entityGroupList.reduce(
      (acc, val, idx) => (idx < i ? acc + drawModel.layout.entityHeight * val.entities.length : acc),
      0,
    );
    // console.log('Updated Y', updatedY);

    return i * updatedY;
  });

  const entityName = leftNavItems
    ?.selectAll('.entity-name')
    .data((d) => [d])
    .join((enter) => {
      return enter
        .append('text')
        .classed('entity-name', true)
        .attr('fill', '#000C79')
        .attr('font-size', 14)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'text-after-edge')
        .attr('text-decoration', (d) => (d.name.isClickable ? 'underline' : ''));
    })
    .attr('x', drawModel.layout.margin.left / 2)
    .attr('y', drawModel.layout.entityHeight)
    .style('cursor', (d) => (d.name.isClickable ? 'pointer' : 'default'))
    .attr('transform', `translate(0, -${drawModel.layout.offsetBottom})`)
    .text((d) => (d.name.displayTxt.length > 12 ? d.name.displayTxt.slice(0, 12) + '...' : d.name.displayTxt))
    .on('click.entity_title', function (e, entityInfo) {
      if (entityInfo.data && entityInfo.name.isClickable) {
        entityEventCb({
          type: 'LEFT_NAV',
          action: 'TITLE_CLICK',
          event: e,
          entityData: entityInfo.data,
        });
      }
    });

  leftNavItems
    ?.selectAll('.entity-subtitle')
    .data((d) => [d])
    .join((enter) => {
      return enter
        .append('text')
        .classed('entity-subtitle', true)
        .attr('fill', 'black')
        .attr('font-size', 14)
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'central')
        .attr('fill', '#636267');
    })
    .attr('x', drawModel.layout.margin.left / 2)
    .attr('y', drawModel.layout.entityHeight / 2)
    .text((d) => d.metadata?.subTitle || '');

  const entityTitle = leftNavItems
    ?.selectAll('.entity-title')
    .data((d) => (d.metadata ? [d] : []))
    .join((enter) => {
      return enter
        .append('text')
        .classed('entity-title', true)
        .attr('fill', 'black')
        .attr('font-size', 14)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'text-before-edge');
    })
    .attr('x', drawModel.layout.margin.left / 2)
    .attr('y', 0)
    .style('cursor', 'help')
    .attr('transform', `translate(0, ${drawModel.layout.offsetBottom})`)
    .text((d) =>
      d?.metadata?.title
        ? d.metadata.title.length > 12
          ? d.metadata.title.slice(0, 12) + '...'
          : d.metadata.title
        : '',
    );

  const leftCount = leftNavItems
    ?.selectAll('.entity-left-count')
    .data((d) => (d.metadata ? [d] : []))
    .join((enter) => {
      return enter
        .append('text')
        .classed('entity-left-count', true)
        .attr('fill', 'black')
        .attr('font-size', 14)
        .attr('font-weight', 'bold')
        .attr('alignment-baseline', 'text-after-edge')
        .attr('fill', '#bf40b2');
    })
    .attr('x', 5)
    .attr('y', drawModel.layout.entityHeight)
    .style('cursor', 'help')
    .attr('transform', `translate(5, -${drawModel.layout.offsetBottom})`)
    .text((d) => d?.metadata?.leftCount || '');

  const rightCount = leftNavItems
    ?.selectAll('.entity-right-count')
    .data((d) => (d.metadata ? [d] : []))
    .join((enter) => {
      return enter
        .append('text')
        .classed('entity-right-count', true)
        .attr('fill', 'black')
        .attr('font-size', 14)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'end')
        .attr('fill', '#00b2bd')
        .attr('alignment-baseline', 'text-after-edge');
    })
    .style('cursor', 'help')
    .attr('y', drawModel.layout.entityHeight)
    .attr('transform', `translate(${drawModel.layout.margin.left - 5}, -${drawModel.layout.offsetBottom})`)
    .text((d) => d?.metadata?.rightCount || '');

  // Add new icon after the entity name
  const entityIcon = leftNavItems
    ?.selectAll('.entity-icon')
    .data((d) => (d.metadata?.icon ? [d] : []))
    .join((enter) => {
      const svg = enter
        .append('svg')
        .attr('width', 16)
        .attr('height', 16)
        .attr('x', drawModel.layout.margin.left - 21)
        .attr('y', drawModel.layout.entityHeight / 2 - drawModel.layout.offsetBottom)
        .attr('viewBox', '0 0 16 16')
        .classed('entity-icon', true)
        .style('cursor', (d) => (d.metadata?.icon?.isClickable ? 'pointer' : 'default'));

      svg
        .append('path')
        .attr('d', (d) => d?.metadata?.icon?.svgPath || '')
        .attr('fill', (d) => d?.metadata?.icon?.fill || '#DB3F27');

      return svg;
    })
    .on('click.entity_icon', function (e, entityInfo) {
      const [x, y] = d3.pointer(e);
      const scaleGroupEntitiesY = d3.scaleLinear(
        [0, drawModel.timelineModel.comptedEntities.totalGroupEntities],
        [0, drawModel.layout.entityHeight * drawModel.timelineModel.comptedEntities.totalEntities],
      );
      const groupIndex = Math.floor(scaleGroupEntitiesY.invert(y));
      if (entityInfo.metadata?.icon?.isClickable) {
        entityEventCb({
          type: 'LEFT_NAV',
          action: 'ICON_CLICK',
          event: e,
          entityData: entityInfo.data,
          entityGroupData: drawModel.timelineModel.entities[groupIndex].data,
          iconName: entityInfo.metadata?.icon?.name,
        });
      }
    });

  return {
    foreground,
    elements,
    body,
    leftNav,
    entityName,
    entityTitle,
    leftCount,
    rightCount,
    entityIcon,
  };
};

export type TTimelineChartGroups = ReturnType<typeof createTimelineChart>;
